import { default as http } from "@/util/http";
import { IRequestResponse } from "@/interfaces/IRequestResponse";

export function isValidConversationId(conversationId: unknown): conversationId is number {
  return typeof conversationId === "number" && Number.isFinite(conversationId) && conversationId > 0;
}

export function parseChatList(response: IRequestResponse.IRequest): {
  conversations: any[];
  unreadCount: number;
  hasMore: boolean;
} {
  const result = response?.result ?? (response as any)?.Result ?? {};
  return {
    conversations: result.conversations || result.Conversations || [],
    unreadCount: Number(result.unreadCount ?? result.UnreadCount ?? 0) || 0,
    hasMore: Boolean(result.hasMore ?? result.HasMore),
  };
}

/** Never treat a failed/unknown envelope as an empty history. */
export function parseMessages(response: IRequestResponse.IRequest): {
  ok: boolean;
  messages: any[];
} {
  if (!response) return { ok: false, messages: [] };
  const success = (response as any).success ?? (response as any).Success;
  if (success === false) return { ok: false, messages: [] };

  const raw = (response as any).result ?? (response as any).Result;
  if (Array.isArray(raw)) return { ok: true, messages: raw };
  if (!raw || typeof raw !== "object") return { ok: false, messages: [] };

  const list =
    raw.messages ??
    raw.Messages ??
    raw.items ??
    raw.Items ??
    raw.$values ??
    raw.values ??
    null;

  if (Array.isArray(list)) return { ok: true, messages: list };
  return { ok: false, messages: [] };
}

export function parseSentMessage(response: IRequestResponse.IRequest): any | null {
  if (!response) return null;
  const success = (response as any).success ?? (response as any).Success;
  if (success === false) return null;
  const raw = (response as any).result ?? (response as any).Result;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw;
}

export function newClientMessageId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export class ChatService {
  static async getConversationsAsync(opts?: {
    skip?: number;
    take?: number;
    unreadOnly?: boolean;
    group?: "platform" | "hire" | string;
  }): Promise<IRequestResponse.IRequest> {
    const params = new URLSearchParams();
    if (opts?.skip != null) params.set("skip", String(opts.skip));
    if (opts?.take != null) params.set("take", String(opts.take));
    if (opts?.unreadOnly) params.set("unreadOnly", "true");
    if (opts?.group) params.set("group", opts.group);
    const query = params.toString();
    const response = await http.get<IRequestResponse.IRequest>(
      query ? `Chat/GetConversationsAsync?${query}` : "Chat/GetConversationsAsync"
    );
    return response.data;
  }

  static async getConversationByIdAsync(conversationId: number): Promise<IRequestResponse.IRequest> {
    if (!isValidConversationId(conversationId)) {
      throw new Error("invalid conversation id");
    }
    const response = await http.get<IRequestResponse.IRequest>(
      `Chat/GetConversationByIdAsync?conversationId=${conversationId}`
    );
    return response.data;
  }

  static async getConversationByHirePublicIdAsync(hirePublicId: string): Promise<IRequestResponse.IRequest> {
    const response = await http.get<IRequestResponse.IRequest>(
      `Chat/GetConversationByHirePublicIdAsync?hirePublicId=${encodeURIComponent(hirePublicId)}`
    );
    return response.data;
  }

  static async getMessagesAsync(conversationId: number, skip = 0, take = 80): Promise<IRequestResponse.IRequest> {
    if (!isValidConversationId(conversationId)) {
      throw new Error("invalid conversation id");
    }
    const response = await http.get<IRequestResponse.IRequest>(
      `Chat/GetMessagesAsync?conversationId=${conversationId}&skip=${skip}&take=${take}`
    );
    return response.data;
  }

  static async sendMessageAsync(
    conversationId: number,
    body: string,
    clientMessageId?: string
  ): Promise<IRequestResponse.IRequest> {
    if (!isValidConversationId(conversationId)) {
      throw new Error("invalid conversation id");
    }
    const response = await http.post<IRequestResponse.IRequest>("Chat/SendMessageAsync", {
      conversationId,
      body,
      clientMessageId: clientMessageId || undefined,
    });
    return response.data;
  }

  static async markReadAsync(conversationId: number): Promise<IRequestResponse.IRequest> {
    if (!isValidConversationId(conversationId)) {
      throw new Error("invalid conversation id");
    }
    const response = await http.post<IRequestResponse.IRequest>("Chat/MarkReadAsync", {
      conversationId,
    });
    return response.data;
  }

  static async getUnreadCountAsync(): Promise<IRequestResponse.IRequest> {
    const response = await http.get<IRequestResponse.IRequest>("Chat/GetUnreadCountAsync");
    return response.data;
  }

  static async searchPlatformParticipantsAsync(opts: {
    q: string;
    skip?: number;
    take?: number;
  }): Promise<IRequestResponse.IRequest> {
    const params = new URLSearchParams();
    params.set("q", opts.q);
    if (opts.skip != null) params.set("skip", String(opts.skip));
    if (opts.take != null) params.set("take", String(opts.take));
    const response = await http.get<IRequestResponse.IRequest>(
      `Chat/SearchPlatformParticipantsAsync?${params.toString()}`
    );
    return response.data;
  }

  static async ensurePlatformConversationForTargetAsync(opts: {
    personId?: number;
    businessUnitId?: number;
  }): Promise<IRequestResponse.IRequest> {
    const response = await http.post<IRequestResponse.IRequest>(
      "Chat/EnsurePlatformConversationForTargetAsync",
      {
        personId: opts.personId || undefined,
        businessUnitId: opts.businessUnitId || undefined,
      }
    );
    return response.data;
  }
}

export type ChatParticipantKind = "professional" | "businessUnit";

export interface ChatParticipantSearchItem {
  kind: ChatParticipantKind | string;
  Kind?: string;
  personId?: number;
  PersonId?: number;
  businessUnitId?: number;
  BusinessUnitId?: number;
  name?: string;
  Name?: string;
  nickname?: string;
  Nickname?: string;
  photoUrl?: string;
  PhotoUrl?: string;
}

export function parseChatParticipantSearch(response: IRequestResponse.IRequest): {
  items: ChatParticipantSearchItem[];
  hasMore: boolean;
  skip: number;
  take: number;
} {
  if (!response) return { items: [], hasMore: false, skip: 0, take: 10 };
  const success = (response as any).success ?? (response as any).Success;
  if (success === false) return { items: [], hasMore: false, skip: 0, take: 10 };
  const raw = (response as any).result ?? (response as any).Result;
  const list = raw?.items ?? raw?.Items ?? (Array.isArray(raw) ? raw : null);
  return {
    items: Array.isArray(list) ? list : [],
    hasMore: Boolean(raw?.hasMore ?? raw?.HasMore),
    skip: Number(raw?.skip ?? raw?.Skip ?? 0) || 0,
    take: Number(raw?.take ?? raw?.Take ?? 10) || 10,
  };
}
